import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchEmploymentJobDetails } from '../services/EmploymentJobService';
import './FlexibleEmployment.scss';

function formatDate(dateValue) {
  if (!dateValue) return 'Not set';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString();
}

function normalizeSkills(job) {
  if (Array.isArray(job?.skillsRequired)) return job.skillsRequired.filter(Boolean);
  if (typeof job?.skillsRequired === 'string') {
    return job.skillsRequired.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

export default function EmploymentJobDetailsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    fetchEmploymentJobDetails(jobId)
      .then((data) => setJob(data || null))
      .catch((fetchError) => {
        console.error('Failed to load employment job details:', fetchError);
        setError('Unable to load this job right now.');
        setJob(null);
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  return (
    <div className="flex-employment-page">
      <div className="flex-employment-shell">
        <div className="flex-employment-hero">
          <div>
            <h1>Job details</h1>
            <p>Review the role requirements before you register and apply as a flexible worker.</p>
          </div>
        </div>

        {loading && <div className="flex-employment-loading">Loading job...</div>}
        {error && <div className="flex-employment-error">{error}</div>}

        {!loading && !error && !job && (
          <div className="flex-employment-empty">This job is no longer available.</div>
        )}

        {!loading && !error && job && (
          <div className="flex-employment-card">
            <div>
              <span className={`flex-employment-status ${(job.jobStatus || 'open').toLowerCase()}`}>{job.jobStatus || 'open'}</span>
            </div>
            <div>
              <h3>{job.jobTitle || 'Untitled temporary role'}</h3>
              <p>{job.jobDescription || 'No description provided.'}</p>
            </div>

            <div className="flex-employment-meta">
              <div>
                <strong>Dates</strong>
                {job.fromDate && job.toDate && (
                  <span>{formatDate(job.fromDate)} to {formatDate(job.toDate)}</span>
                )}
              </div>
              <div>
                <strong>Closing date</strong>
                <span>{formatDate(job.adClosingDate)}</span>
              </div>
              <div>
                <strong>Location</strong>
                <span>{job.jobLocation || job.cityName || 'Not set'}</span>
              </div>
              <div>
                <strong>No of people required</strong>
                <span>{job.numberOfPeopleRequired || 1}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{job.employmentType || 'Not set'}</span>
              </div>
              <div>
                {job.hourlyRate && (
                  <>
                    <strong>Hourly rate</strong>
                    <span>{job.hourlyRate ? `$${job.hourlyRate}` : 'Not set'}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex-employment-skills">
              {normalizeSkills(job).length > 0 ? normalizeSkills(job).map((skill) => (
                <span className="flex-employment-skill" key={skill}>{skill}</span>
              )) : <span>No skills listed</span>}
            </div>

            <div className="flex-employment-card-actions">
              <button className="flex-employment-button" type="button" onClick={() => navigate(`/employment-jobs/${jobId}/apply`)}>
                Apply for this job
              </button>
              <button className="flex-employment-button-secondary" type="button" onClick={() => navigate('/jobs')}>
                Back to jobs
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}